import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Assets } from '../assets';

export interface PictureImage {
  src: string;
  alt: string;
}

export enum PictureDisplay {
  INLINE = 'inline',
  BLOCK = 'block',
}

export enum PictureObjectFit {
  CONTAIN = 'contain',
  COVER = 'cover',
  NONE = 'none',
  SCALE_DOWN = 'scale-down',
}

@Component({
  selector: 'fl-picture',
  template: `
    <picture
      class="PictureElement"
      [attr.data-display]="display"
      [attr.data-bounded-height]="boundedHeight"
    >
      <source [srcset]="pictureSource" />
      <img
        class="ImageElement"
        [attr.data-display]="display"
        [attr.data-full-width]="fullWidth"
        [attr.data-bounded-width]="boundedWidth"
        [attr.data-bounded-height]="boundedHeight"
        [attr.data-align-center]="alignCenter"
        [attr.data-object-fit]="objectFit"
        [src]="pictureSource"
        [alt]="alt"
        (load)="handleImageLoad()"
        (error)="handleImageError()"
      />
    </picture>
  `,
  styleUrls: ['./picture.component.scss'],
})
export class PictureComponent implements OnChanges {
  @Input() src: string;
  @Input() alt: string;

  /** Forces the image to always have 100% of the width of its parent,
   * no matter how big or small it is */
  @Input() fullWidth: boolean;

  /** This ensures that the image will not overflow to its parent container by adding a max-width 100%
   * This mostly useful if you want your image to adjust accordingly on smaller
   * screens while keeping its original width on bigger screens. */
  @Input() boundedWidth?: boolean;

  /** This ensures that the image will not overflow to its parent container's height */
  @Input() boundedHeight?: boolean;

  /** Center aligns the image by setting the horizontal margins to auto */
  @Input() alignCenter?: boolean;

  /** Support external source of the image */
  @Input() externalSrc = false;

  /** Change the object-fit of the img element inside the component */
  @Input() objectFit?: PictureObjectFit;

  /** Fallback image source if image doesn't load */
  @Input() fallbackSrc: string;

  @HostBinding('attr.data-display')
  @Input()
  display: PictureDisplay;

  @Output() imageLoaded = new EventEmitter<void>();
  @Output() onError = new EventEmitter<void>();

  pictureSource: string;

  constructor(private assets: Assets) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('src' in changes || 'externalSrc' in changes) {
      this.pictureSource = this.externalSrc
        ? this.src
        : this.assets.getUrl(this.src);
    }
  }

  handleImageLoad() {
    this.imageLoaded.emit();
  }

  handleImageError() {
    if (this.fallbackSrc) {
      this.pictureSource = this.assets.getUrl(this.fallbackSrc);
    }

    this.onError.emit();
  }
}
